/**
 * The plan redrawn as it was drawn: its line work as true straight lines and circular arcs, each at
 * its own weight and tone, meeting exactly where they meet — rebuilt from the screenshot, not traced
 * pixel by pixel.
 *
 *  1. The line work's skeleton is cut into chains between its junctions.
 *  2. Along each chain the line is measured across, on the full-resolution plan (centres.ts): the
 *     ink's darkness profile gives the line's true centre (to a small fraction of a pixel), its
 *     width and its tone. Where the profile is not one clean line (a junction, a crossing, a fill,
 *     a corner the chain turns) the point is set aside.
 *  3. The centres are cut into the fewest lines and arcs that keep within a third of a pixel of
 *     them (fit.ts), a corner kept sharp.
 *  4. At every junction the pieces are put back together: a line that passes through (an X, the
 *     bar of a T) is one line across it, a line ending on another meets it exactly, two lines
 *     meeting at a corner meet at their true corner, and a line's free end is where its ink ends.
 *  5. What no line draws (letters, arrowheads, solid blocks, symbols) is outlined; leaders are told
 *     from the plan's own lines by the labels they come from; dashes are one dashed line; and the
 *     plan's shading is laid under it all.
 *
 * All positions are image px (the plan at full resolution); the skeleton is in working px (scale k).
 */
import type { Luma } from './ink.ts';
import { lumaAt } from './ink.ts';
import { fitCircle, fitLine, type ArcFit, type Fit, type LineFit } from './fit.ts';
import { centresAlong, piecesOf, type Plate } from './centres.ts';
import type { PlanRaster } from './types.ts';
import { boxBlur, keepEveryMark, minimalSkeleton, pruneSpurs, thin, type Component, type Mask } from './raster.ts';
import { isoRings, ringArea, simplifyRing } from './contour.ts';
import { findTextLines } from './labels.ts';
import { followLeader } from './stations.ts';

export type Role = 'plan' | 'leader';

export interface DLine {
  t: 'line';
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  w: number; // width, image px
  v: number; // darkness 0–1 (1: black on the paper)
  role: Role;
  /** A dashed line: the stretches along it (px from its first end) that are drawn. */
  dash?: number[];
}

export interface DArc {
  t: 'arc';
  cx: number;
  cy: number;
  r: number;
  a0: number; // from angle a0 to a1 (radians, image coordinates: y down), either way round
  a1: number;
  w: number;
  v: number;
  role: Role;
  dash?: number[];
}

export interface DFill {
  t: 'fill';
  /** Closed outlines (image px), filled even-odd: a symbol, an arrowhead, a solid block, a mark that is no line. */
  rings: number[][];
  v: number;
  role: Role | 'arrowhead' | 'symbol' | 'shade';
}

export interface DText {
  t: 't';
  /** Its line: baseline (image px), cap height, and each glyph's box and reading ('' where unread). */
  base: number;
  h: number;
  glyphs: { x0: number; y0: number; x1: number; y1: number; ch: string }[];
  v: number;
}

export type DItem = DLine | DArc | DFill | DText;

/** A leader: the thin line from a label to what it labels (its landing at the text, its bend, its tip; image px). */
export interface Leader {
  pts: number[];
  text: { x0: number; y0: number; x1: number; y1: number };
}

export interface Drawing {
  w: number;
  h: number;
  items: DItem[];
  /** The plan's leaders, as followed from their labels: never line work to snap to. */
  leaders: Leader[];
  ms: number;
  /** How long each stage took (ms). */
  times?: Record<string, number>;
}

/* ------------------------------------------------------------------ chains */

interface Chain {
  px: Int32Array; // working pixel indices, node pixel at each end (for a loop: first = last)
  a: number; // node ids (-1: none, a closed loop)
  b: number;
}

interface NodeC {
  x: number; // image px (centroid of its pixels)
  y: number;
  ends: number; // chain ends meeting here
}

/** The skeleton cut into chains between its junctions and ends (working px). */
export function chainsOf(skel: Uint8Array, w: number, h: number, k: number): { chains: Chain[]; nodes: NodeC[] } {
  const n8 = [-w - 1, -w, -w + 1, -1, 1, w - 1, w, w + 1];
  const deg = (i: number) => {
    let c = 0;
    for (const d of n8) c += skel[i + d]!;
    return c;
  };
  const nodeAt = new Int32Array(w * h).fill(-1);
  const isNode = new Uint8Array(w * h);
  for (let y = 1; y < h - 1; y++)
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      if (skel[i] && deg(i) !== 2) isNode[i] = 1;
    }
  const nodes: NodeC[] = [];
  const stack: number[] = [];
  for (let i = 0; i < w * h; i++) {
    if (!isNode[i] || nodeAt[i]! >= 0) continue;
    const id = nodes.length;
    let sx = 0,
      sy = 0,
      c = 0;
    nodeAt[i] = id;
    stack.push(i);
    while (stack.length) {
      const p = stack.pop()!;
      sx += p % w;
      sy += Math.floor(p / w);
      c++;
      for (const d of n8) {
        const q = p + d;
        if (isNode[q] && nodeAt[q]! < 0) {
          nodeAt[q] = id;
          stack.push(q);
        }
      }
    }
    nodes.push({ x: (sx / c + 0.5) / k, y: (sy / c + 0.5) / k, ends: 0 });
  }
  const seen = new Uint8Array(w * h);
  const chains: Chain[] = [];
  for (let i = 0; i < w * h; i++) {
    if (!isNode[i]) continue;
    for (const d of n8) {
      const s0 = i + d;
      if (!skel[s0] || isNode[s0] || seen[s0]) continue;
      const path = [i, s0];
      seen[s0] = 1;
      let prev = i,
        cur = s0,
        end = -1;
      for (let guard = 0; guard < w * h; guard++) {
        let next = -1;
        for (const dd of n8) {
          const q = cur + dd;
          if (!skel[q] || q === prev) continue;
          if (isNode[q]) {
            if (nodeAt[q] === nodeAt[i] && path.length < 3) continue;
            end = q;
            break;
          }
          if (!seen[q]) next = q;
        }
        if (end >= 0) {
          path.push(end);
          break;
        }
        if (next < 0) break;
        seen[next] = 1;
        path.push(next);
        prev = cur;
        cur = next;
      }
      const a = nodeAt[i]!,
        b = end >= 0 ? nodeAt[end]! : -1;
      nodes[a]!.ends++;
      if (b >= 0) nodes[b]!.ends++;
      chains.push({ px: Int32Array.from(path), a, b });
    }
  }
  // closed loops (a circle drawn on its own)
  for (let i = 0; i < w * h; i++) {
    if (!skel[i] || seen[i] || isNode[i]) continue;
    const path = [i];
    seen[i] = 1;
    let prev = -1,
      cur = i;
    for (let guard = 0; guard < w * h; guard++) {
      let next = -1;
      for (const d of n8) {
        const q = cur + d;
        if (skel[q] && q !== prev && !seen[q]) {
          next = q;
          break;
        }
      }
      if (next < 0) break;
      seen[next] = 1;
      path.push(next);
      prev = cur;
      cur = next;
    }
    path.push(i);
    chains.push({ px: Int32Array.from(path), a: -1, b: -1 });
  }
  return { chains, nodes };
}

/* ------------------------------------------------------------------ building */

interface Prim {
  fit: Fit;
  xs: Float64Array;
  ys: Float64Array;
  w: number;
  v: number;
  /** The node at its first and last end (-1: none: it ends inside a chain, at another piece). */
  na: number;
  nb: number;
  /** Its ends (image px), once placed. */
  ax: number;
  ay: number;
  bx: number;
  by: number;
  dead?: boolean;
  /** The piece it was joined into. */
  succ?: Prim;
  id: number;
}

export interface DrawingInput {
  plan: PlanRaster;
  luma: Luma;
  lumaK: number; // luma px per image px
  paper: Uint8Array; // working px: the paper's tone
  grain: Uint8Array; // working px: how much darker than its paper ink must be there (levels)
  /** The ink's components (working px) and which of them are letters. */
  labels: Int32Array;
  comps: Component[];
  text: Set<number>;
  /** The plan's lightness on the working raster. */
  lum: Uint8Array;
}

/**
 * The line work, sharply: a pixel is ink where it is at least half as dark (against its paper) as the
 * darkest ink within two pixels of it — so two lines a pixel or two apart stay two lines, and a faint
 * line alone is still a line. Letters are left out (they are outlined whole). Working px, 1 = ink.
 */
export function sharpInk(inp: Pick<DrawingInput, 'plan' | 'paper' | 'grain' | 'lum' | 'labels' | 'text'>): Uint8Array {
  const { plan, paper, grain, lum, labels, text } = inp;
  const { cw, ch } = plan;
  const n = cw * ch;
  const d = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const P = paper[i]!;
    // (no darker than the paper's own grain: no ink)
    d[i] = P <= 96 || P - lum[i]! < grain[i]! ? 0 : Math.max(0, (P - lum[i]!) / P);
  }
  const peak = localMax(d, cw, ch, 2);
  const out = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    if (d[i]! < 0.1 || d[i]! < 0.6 * peak[i]!) continue;
    const l = labels[i]!;
    if (l && text.has(l)) continue;
    out[i] = 1;
  }
  return out;
}

const median = (a: number[]): number => {
  if (!a.length) return 0;
  const s = a.slice().sort((p, q) => p - q);
  return s[s.length >> 1]!;
};

/** The plan's line work as lines and arcs (see the file's head). */
export function buildDrawing(inp: DrawingInput): Drawing {
  const t0 = performance.now();
  const { plan, luma, lumaK, paper } = inp;
  const { cw, ch, k } = plan;
  const paperAt = (x: number, y: number) => {
    const X = Math.min(cw - 1, Math.max(0, Math.floor(x * k))),
      Y = Math.min(ch - 1, Math.max(0, Math.floor(y * k)));
    return paper[Y * cw + X]!;
  };
  // the sharp line work, thinned to its skeleton (short spurs only trimmed: a zigzag's teeth are its own)
  const times: Record<string, number> = {};
  let tl = performance.now();
  const lap = (name: string) => {
    const now = performance.now();
    times[name] = Math.round(now - tl);
    tl = now;
  };
  const ink = sharpInk(inp);
  lap('ink');
  const sk: Mask = thin({ w: cw, h: ch, data: ink });
  lap('thin');
  keepEveryMark({ w: cw, h: ch, data: ink }, sk);
  minimalSkeleton(sk);
  pruneSpurs(sk, 3);
  lap('skeleton');
  const { chains, nodes } = chainsOf(sk.data, cw, ch, k);
  lap('chains');
  const prims: Prim[] = [];
  const plate: Plate = { L: luma, lk: lumaK, paperAt, buf: new Float32Array(512) };
  for (const c of chains) {
    const n = c.px.length;
    if (n < 3) continue;
    const px = new Float64Array(n),
      py = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      const p = c.px[i]!;
      px[i] = ((p % cw) + 0.5) / k;
      py[i] = (Math.floor(p / cw) + 0.5) / k;
    }
    // each point measured square to the rough run it lies on (never along a tooth of a zigzag or
    // into a corner), in runs parted at the chain's corners
    const runs = centresAlong(px, py, c.a < 0, plate, { roughTol: Math.max(0.75, 0.8 / k), skipEnds: 1, R: 5, Rwide: 14, maxOff: 1e9 });
    const pieces = piecesOf(runs, (w) => Math.max(0.35, 0.15 * w), c.a < 0);
    pieces.forEach((pc, j) => {
      // a piece whose width grows steadily along it is no stroke (an arrowhead, a wedge): an outline.
      // (Its ends are left out: where pieces meet at a corner the ink is wider across.)
      if (isWedge(pc.ws)) return;
      const pr: Prim = { id: prims.length, fit: pc.fit, xs: pc.xs, ys: pc.ys, w: median(pc.ws), v: median(pc.vs), na: j === 0 ? c.a : -1, nb: j === pieces.length - 1 ? c.b : -1, ax: pc.ax, ay: pc.ay, bx: pc.bx, by: pc.by };
      prims.push(pr);
    });
  }
  lap('measure+fit');
  joinAtNodes(prims, nodes, luma, lumaK, paperAt);
  lap('join');
  let items: DItem[] = [];
  for (const p of prims) {
    if (p.dead) continue;
    // a stroke must be a line: longer than it is wide (a speck, a zigzag's tooth, a dot is outlined
    // instead), and solid ink all along its middle (a hatched or dotted block is outlined instead)
    const len = p.fit.t === 'arc' ? p.fit.r * Math.abs(p.fit.a1 - p.fit.a0) : Math.hypot(p.bx - p.ax, p.by - p.ay);
    if (len < Math.max(3, 2.5 * p.w)) continue;
    if (p.w > 3 && !solidUnder(p, luma, lumaK, paperAt)) continue;
    items.push(toItem(p));
  }
  const drop = new Set<DItem>();
  const fills = outlinesOf(inp, ink, items, drop, lap);
  items = items.filter((it) => !drop.has(it));
  items.push(...fills);
  const leaders = markLeaders(inp, items);
  lap('leaders');
  // the plan's shading, under everything
  items.unshift(...shadingOf(inp));
  lap('shading');
  items = groupDashes(items);
  lap('dashes');
  return { w: plan.w, h: plan.h, items, leaders, ms: Math.round(performance.now() - t0), times };
}

/* ------------------------------------------------------------------ what is no line */

/**
 * Everything of the plan's ink the lines and arcs do not draw — letters, arrowheads, solid blocks,
 * symbols, anything the plan holds that is no line — as filled outlines: the lines are laid on the
 * ink (working px), and each part of the ink left over is outlined on the full-resolution plan.
 */
function outlinesOf(inp: DrawingInput, lineMask: Uint8Array, strokes: DItem[], drop: Set<DItem>, lap: (name: string) => void = () => {}): DFill[] {
  const { plan, luma, lumaK, labels, comps, text } = inp;
  const { cw, ch, k } = plan;
  // the lines, laid on the working raster as the darkness they draw (their edges fading over a pixel)
  const cov = new Float32Array(cw * ch);
  const lay = (x: number, y: number, dist: number, half: number, v: number) => {
    const d = dist <= half ? v : dist < half + k ? v * (1 - (dist - half) / k) : 0;
    const i = y * cw + x;
    if (d > cov[i]!) cov[i] = d;
  };
  for (const it of strokes) {
    if (it.t !== 'line' && it.t !== 'arc') continue;
    const half = (it.w / 2) * k,
      rad = half + k;
    if (it.t === 'line') {
      const x1 = it.x1 * k,
        y1 = it.y1 * k,
        x2 = it.x2 * k,
        y2 = it.y2 * k;
      const ex = x2 - x1,
        ey = y2 - y1,
        l2 = ex * ex + ey * ey || 1e-9;
      const len = Math.sqrt(l2);
      const bx0 = Math.min(x1, x2) - rad,
        bx1 = Math.max(x1, x2) + rad;
      // row by row, only across the band the line's reach makes there
      for (let y = Math.max(0, Math.floor(Math.min(y1, y2) - rad)); y <= Math.min(ch - 1, Math.ceil(Math.max(y1, y2) + rad)); y++) {
        const py = y + 0.5;
        let xa = bx0,
          xb = bx1;
        if (Math.abs(ey) > 1e-9 * len) {
          const xc = x1 + ((py - y1) * ex) / ey,
            hw = (rad * len) / Math.abs(ey) + 1;
          xa = Math.max(xa, xc - hw);
          xb = Math.min(xb, xc + hw);
        }
        for (let x = Math.max(0, Math.floor(xa)); x <= Math.min(cw - 1, Math.ceil(xb)); x++) {
          const px = x + 0.5;
          const t = Math.max(0, Math.min(1, ((px - x1) * ex + (py - y1) * ey) / l2));
          const dx = px - x1 - ex * t,
            dy = py - y1 - ey * t;
          const d2 = dx * dx + dy * dy;
          if (d2 < rad * rad) lay(x, y, Math.sqrt(d2), half, it.v);
        }
      }
    } else {
      const cx = it.cx * k,
        cy = it.cy * k,
        r = it.r * k;
      const lo = Math.min(it.a0, it.a1),
        hi = Math.max(it.a0, it.a1);
      // its own bounds: its ends, and the points due north, south, east and west it passes
      let by0 = Math.min(cy + r * Math.sin(lo), cy + r * Math.sin(hi)),
        by1 = Math.max(cy + r * Math.sin(lo), cy + r * Math.sin(hi));
      let bx0 = Math.min(cx + r * Math.cos(lo), cx + r * Math.cos(hi)),
        bx1 = Math.max(cx + r * Math.cos(lo), cx + r * Math.cos(hi));
      for (let q = Math.ceil(lo / (Math.PI / 2)); q * (Math.PI / 2) <= hi; q++) {
        const a = q * (Math.PI / 2);
        bx0 = Math.min(bx0, cx + r * Math.cos(a));
        bx1 = Math.max(bx1, cx + r * Math.cos(a));
        by0 = Math.min(by0, cy + r * Math.sin(a));
        by1 = Math.max(by1, cy + r * Math.sin(a));
      }
      const ro = r + rad,
        ri = Math.max(0, r - rad);
      const visit = (x: number, y: number) => {
        const px = x + 0.5,
          py = y + 0.5;
        const dr = Math.abs(Math.hypot(px - cx, py - cy) - r);
        if (dr > rad) return;
        let a = Math.atan2(py - cy, px - cx);
        while (a < lo) a += 2 * Math.PI;
        while (a > lo + 2 * Math.PI) a -= 2 * Math.PI;
        if (a > hi) {
          // past its ends: the distance to the nearer end
          const e0 = Math.hypot(px - cx - r * Math.cos(lo), py - cy - r * Math.sin(lo)),
            e1 = Math.hypot(px - cx - r * Math.cos(hi), py - cy - r * Math.sin(hi));
          lay(x, y, Math.min(e0, e1), half, it.v);
        } else lay(x, y, dr, half, it.v);
      };
      // row by row, only across the ring its reach makes there (one span, or two either side of the middle)
      for (let y = Math.max(0, Math.floor(by0 - rad)); y <= Math.min(ch - 1, Math.ceil(by1 + rad)); y++) {
        const dy = y + 0.5 - cy;
        if (Math.abs(dy) > ro + 1) continue;
        const wo = Math.sqrt(Math.max(0, ro * ro - dy * dy)) + 1;
        const wi = Math.abs(dy) < ri - 1 ? Math.sqrt(ri * ri - dy * dy) - 1 : -1;
        const xa = Math.max(0, Math.floor(bx0 - rad), Math.floor(cx - wo)),
          xb = Math.min(cw - 1, Math.ceil(bx1 + rad), Math.ceil(cx + wo));
        if (wi <= 0) for (let x = xa; x <= xb; x++) visit(x, y);
        else {
          for (let x = xa; x <= Math.min(xb, Math.ceil(cx - wi)); x++) visit(x, y);
          for (let x = Math.max(xa, Math.floor(cx + wi)); x <= xb; x++) visit(x, y);
        }
      }
    }
  }
  // the plan's own darkness on the working raster, and whether the lines leave it out: clearly
  // darker than they draw it, for its own tone (light grey ink as much as black)
  const dk = new Float32Array(cw * ch);
  for (let i = 0; i < cw * ch; i++) {
    const P = inp.paper[i]!;
    dk[i] = P <= 96 || P <= inp.lum[i]! ? 0 : (P - inp.lum[i]!) / P;
  }
  const darkAt = (i: number) => dk[i]!;
  // (a line's soft edge falls within a pixel of where it is drawn: each pixel is set against the
  // darkest the lines draw within a pixel of it)
  const near = localMax(cov, cw, ch, 1);
  const leftOut = (i: number) => {
    const d = darkAt(i);
    return d - near[i]! > Math.max(0.08, 0.45 * d);
  };
  lap('coverage');
  // the ink left over: letters (all of them), and line work the lines leave out
  const left = new Uint8Array(cw * ch);
  const isText = new Uint8Array(comps.length + 2);
  for (const t of text) if (t < isText.length) isText[t] = 1;
  for (let i = 0; i < cw * ch; i++) {
    const l = labels[i]!;
    if (l && isText[l]) left[i] = 2;
    else if (lineMask[i] && leftOut(i)) left[i] = 1;
  }
  // a line drawn mostly through ink it leaves out (a textured band, a pattern, lines too close to
  // part) explains nothing there: it goes, and all the ink under it is outlined with the rest
  // (a wide stroke: within a pixel or two of its edge, so one laid along a solid mass has the mass's
  // ink beside it)
  const leftNear = (x: number, y: number, r: number): boolean => {
    const X = Math.floor(x * k),
      Y = Math.floor(y * k);
    for (let dy = -r; dy <= r; dy++)
      for (let dx = -r; dx <= r; dx++) {
        const xx = X + dx,
          yy = Y + dy;
        if (xx >= 0 && yy >= 0 && xx < cw && yy < ch && left[yy * cw + xx] === 1) return true;
      }
    return false;
  };
  const footprint = (it: DLine | DArc, visit: (i: number) => void) => {
    const half = (it.w / 2 + 0.7) * k;
    const pt = (x: number, y: number) => {
      const X = x * k,
        Y = y * k;
      for (let yy = Math.floor(Y - half); yy <= Math.ceil(Y + half); yy++)
        for (let xx = Math.floor(X - half); xx <= Math.ceil(X + half); xx++) if (xx >= 0 && yy >= 0 && xx < cw && yy < ch && Math.hypot(xx + 0.5 - X, yy + 0.5 - Y) <= half) visit(yy * cw + xx);
    };
    sampleAlong(it, 0.7 / k, pt);
  };
  for (const it of strokes) {
    if (it.t !== 'line' && it.t !== 'arc') continue;
    let n = 0,
      bad = 0;
    const r = it.w > 3 ? Math.max(1, Math.round((it.w / 2 + 2.5) * k)) : 1;
    sampleAlong(it, 1 / k, (x, y) => {
      n++;
      if (leftNear(x, y, r)) bad++;
    });
    if (n && bad >= 0.3 * n) {
      drop.add(it);
      footprint(it, (i) => {
        if (lineMask[i] && left[i] !== 2) left[i] = 1;
      });
    }
  }
  lap('left over');
  const out: DFill[] = [];
  const seen = new Uint8Array(cw * ch);
  const q: number[] = [];
  for (let s = 0; s < cw * ch; s++) {
    if (!left[s] || seen[s]) continue;
    // one part: its pixels, bounds, and how much of it lies along a line (a sliver the line nearly covers)
    const part: number[] = [];
    let minX = cw,
      minY = ch,
      maxX = 0,
      maxY = 0,
      beside = 0,
      letters = 0;
    seen[s] = 1;
    q.push(s);
    while (q.length) {
      const p = q.pop()!;
      part.push(p);
      const x = p % cw,
        y = (p - x) / cw;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      if (left[p] === 2) letters++;
      let touches = false;
      for (let dy = -1; dy <= 1; dy++)
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx,
            yy = y + dy;
          if (xx < 0 || yy < 0 || xx >= cw || yy >= ch) continue;
          const j = yy * cw + xx;
          if (cov[j]! > 0.2) touches = true;
          // (one patch is one tone: grey ink beside black is a patch of its own, outlined in its own tone)
          if (left[j] && !seen[j] && (left[j] === 2) === (left[p] === 2) && Math.abs(darkAt(j) - darkAt(p)) <= Math.max(0.15, 0.4 * Math.max(darkAt(j), darkAt(p)))) {
            seen[j] = 1;
            q.push(j);
          }
        }
      if (touches) beside++;
    }
    if (!letters) {
      // (a speck is noise — but not a small dot of a mark the ink holds whole: a colon's, a period's)
      if (part.length < 3) {
        const l = labels[part[0]!]!;
        if (!l || comps[l - 1]!.count < 3) continue;
      }
      // a sliver along a line (its ink a shade wider than drawn) is that line's, not a shape of its own:
      // it has no inside (no pixel of it ringed all round by its own ink)
      let solid = 0;
      for (const p of part) {
        const x = p % cw;
        if (x > 0 && x < cw - 1 && p >= cw && p < cw * (ch - 1) && left[p - 1] && left[p + 1] && left[p - cw] && left[p + cw] && left[p - cw - 1] && left[p - cw + 1] && left[p + cw - 1] && left[p + cw + 1]) solid++;
      }
      if (beside > 0.6 * part.length && solid === 0) continue;
    }
    // its outline on the full-resolution plan: the ink's darkness, where this part lies (a pixel's grace round it)
    const x0 = Math.max(0, Math.floor((minX - 1) / k)),
      y0 = Math.max(0, Math.floor((minY - 1) / k)),
      x1 = Math.min(plan.w - 1, Math.ceil((maxX + 2) / k)),
      y1 = Math.min(plan.h - 1, Math.ceil((maxY + 2) / k));
    const W = x1 - x0 + 1,
      H = y1 - y0 + 1;
    const mine = new Uint8Array((maxX - minX + 3) * (maxY - minY + 3));
    const mw = maxX - minX + 3;
    for (const p of part) {
      const x = p % cw,
        y = (p - x) / cw;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) mine[(y - minY + 1 + dy) * mw + (x - minX + 1 + dx)] = 1;
    }
    const f = new Float32Array(W * H);
    const mh = maxY - minY + 3;
    // (each full-resolution pixel's working pixel, along a row and down the columns, worked out once)
    const colX = new Int32Array(W);
    for (let x = 0; x < W; x++) colX[x] = Math.floor((x0 + x + 0.5) * k);
    let any = false;
    for (let y = 0; y < H; y++) {
      const Yw = Math.floor((y0 + y + 0.5) * k);
      const Y = Yw - minY + 1;
      if (Y < 0 || Y >= mh) continue;
      const prow = Math.min(ch - 1, Yw) * cw;
      const lrow = (y0 + y) * luma.w;
      for (let x = 0; x < W; x++) {
        const Xw = colX[x]!;
        const X = Xw - minX + 1;
        if (X < 0 || X >= mw || !mine[Y * mw + X]) continue;
        const P = Math.max(40, inp.paper[prow + Math.min(cw - 1, Xw)]!);
        const l = lumaK === 1 ? luma.data[lrow + x0 + x]! : lumaAt(luma, (x0 + x + 0.5) * lumaK, (y0 + y + 0.5) * lumaK);
        const d = (P - l) / P;
        if (d > 0) {
          f[y * W + x] = d > 1 ? 1 : d;
          if (d > 0.1) any = true;
        }
      }
    }
    if (!any) continue;
    // its outlines, in layers of tone: the lightest layer's outline taken where the ink first rises
    // above paper, each darker layer's inside it — so grey ink and black ink crossing or touching
    // keep their own tones (each layer drawn over the one before). Where ink is one tone (a letter, a
    // solid block) it is one layer, outlined where it falls to half its darkness.
    const peak = localMax(f, W, H, 2);
    // the tones its ink comes in (its cores: ink at least half as dark as the ink round it)
    const cores: number[] = [];
    for (let i = 0; i < W * H; i++) if (f[i]! >= 0.1 && f[i]! >= 0.6 * peak[i]!) cores.push(f[i]!);
    if (!cores.length) continue;
    // (a letter, or a mark too small to hold more than one tone, is one tone)
    const tones = letters || cores.length < 40 ? [cores.reduce((a, b) => a + b, 0) / cores.length] : toneClusters(cores);
    for (let li = 0; li < tones.length; li++) {
      const c = tones[li]!;
      const lo = li === 0 ? 0.08 : (tones[li - 1]! + c) / 2;
      const g = new Float32Array(W * H);
      // the edge: half-way up to the ink round it (its own tone at most, never below this layer's floor)
      for (let i = 0; i < W * H; i++) g[i] = f[i]! - Math.max(lo, 0.5 * Math.min(peak[i]!, 1.2 * c));
      const raw = isoRings(g, W, H, 0, x0, y0);
      const rings = raw.map((r) => simplifyRing(r, 0.2)).filter((r) => Math.abs(ringArea(r)) >= 0.5);
      if (!rings.length) continue;
      const outer = rings.filter((r) => ringArea(r) > 0),
        holes = rings.filter((r) => ringArea(r) <= 0);
      const groups = outer.map((r) => ({ rings: [r], area: ringArea(r) }));
      groups.sort((p2, q2) => p2.area - q2.area);
      for (const hole of holes) {
        const g2 = groups.find((gr) => pointInRing(hole[0]!, hole[1]!, gr.rings[0]!));
        if (g2) g2.rings.push(hole);
      }
      for (const gr of groups) out.push({ t: 'fill', rings: gr.rings, v: Math.min(1, c), role: letters ? 'symbol' : 'symbol' });
    }
  }
  lap('outlines');
  return out;
}

/**
 * The plan's shading — the tone its paper takes where it is not white (a paved area, a band, a
 * tinted sheet) — as flat fills in each of the few tones it comes in, lightest first (each darker
 * one laid over the lighter round it), outlined smoothly where the tone changes.
 */
function shadingOf(inp: DrawingInput): DFill[] {
  const { plan, paper, labels, lum, grain } = inp;
  const k = plan.k / 2;
  // the plan's own lightness where it has no ink (so a small white island in the shading, a symbol's
  // white middle, is white), the paper's tone under its ink (a line across shading is shading under
  // it) and on grainy shading; a filled symbol's, or the screen round the plan, is no shading. At half the working scale
  // (shading has no fine detail), smoothed (its grain, and its paper's blocky reading)
  const cw = plan.cw >> 1,
    ch = plan.ch >> 1;
  const n = cw * ch;
  const src = new Uint8Array(n);
  const W0 = plan.cw;
  const inked = (i: number) => labels[i]! > 0 || labels[i - 1]! > 0 || labels[i + 1]! > 0 || labels[i - W0]! > 0 || labels[i + W0]! > 0;
  // (grainy shading's own lightness is its grain: its paper's tone, read through the grain, is it)
  const tone = (i: number) => (paper[i]! <= 96 ? 255 : grain[i]! >= 12 || (i > W0 && i < labels.length - W0 && inked(i)) ? paper[i]! : lum[i]!);
  for (let y = 0; y < ch; y++)
    for (let x = 0; x < cw; x++) {
      const i = 2 * y * plan.cw + 2 * x;
      src[y * cw + x] = (tone(i) + tone(i + 1) + tone(i + plan.cw) + tone(i + plan.cw + 1) + 2) >> 2;
    }
  const sm = boxBlur(src, cw, ch, 2);
  const hist = new Float64Array(256);
  for (let i = 0; i < n; i++) hist[sm[i]!]!++;
  // the paper's white (off-white on a scan): the tone most of the lightest paper has
  let white = 255,
    acc = 0;
  for (let v = 255; v >= 0; v--) if ((acc += hist[v]!) >= 0.3 * n) {
    white = v;
    break;
  }
  // the tones the shading takes: peaks of the tones clearly darker than white, each over a fair area
  const lo = 97,
    hi = white - 16;
  const peaks: { v: number; mass: number }[] = [];
  for (let v = lo; v <= hi; v++) {
    let mass = 0,
      top = true;
    for (let d = -6; d <= 6; d++) {
      const u = v + d;
      if (u < lo || u > hi) continue;
      mass += hist[u]!;
      if (d && hist[u]! > hist[v]!) top = false;
    }
    if (top && mass >= 0.003 * n) peaks.push({ v, mass });
  }
  peaks.sort((a, b) => b.mass - a.mass);
  const tones: number[] = [];
  for (const p of peaks) if (tones.length < 4 && tones.every((t) => Math.abs(t - p.v) >= 14)) tones.push(p.v);
  tones.sort((a, b) => b - a);
  const out: DFill[] = [];
  const g = new Float32Array(n);
  let prev = white;
  for (const tone of tones) {
    const level = (prev + tone) / 2;
    prev = tone;
    for (let i = 0; i < n; i++) g[i] = level - sm[i]!;
    // (shading has an edge: the tone steps there, across a few pixels — where it only fades, over
    // many, it is the light the plan was photographed in)
    const step = (prev - tone) / 5;
    const edged = (r: number[]) => {
      let sum = 0,
        cnt = 0;
      for (let i = 0; i < r.length; i += 2) {
        const x = Math.min(cw - 2, Math.max(1, Math.floor(r[i]!))),
          y = Math.min(ch - 2, Math.max(1, Math.floor(r[i + 1]!)));
        const j = y * cw + x;
        sum += Math.hypot(sm[j + 1]! - sm[j - 1]!, sm[j + cw]! - sm[j - cw]!) / 2;
        cnt++;
      }
      return cnt > 0 && sum / cnt >= 0.35 * step;
    };
    const rings = isoRings(g, cw, ch, 0, 0, 0)
      .filter((r) => (ringArea(r) > 0 ? ringArea(r) >= 225 && edged(r) : -ringArea(r) >= 12))
      .map((r) => simplifyRing(r, 0.4).map((c) => c / k));
    const outer = rings.filter((r) => ringArea(r) > 0),
      holes = rings.filter((r) => ringArea(r) <= 0);
    const groups = outer.map((r) => ({ rings: [r], area: ringArea(r) }));
    groups.sort((a, b) => a.area - b.area);
    for (const h of holes) groups.find((gr) => pointInRing(h[0]!, h[1]!, gr.rings[0]!))?.rings.push(h);
    for (const gr of groups) out.push({ t: 'fill', rings: gr.rings, v: 1 - tone / 255, role: 'shade' });
  }
  return out;
}

/**
 * The distinct tones a patch's ink comes in, lightest first: one, or two or three when its ink falls
 * clearly into as many (grey ink beside black), each well apart and holding a fair share of it.
 */
function toneClusters(vals: number[]): number[] {
  const n = vals.length;
  const sorted = Float64Array.from(vals).sort();
  // running sums: the squared error of any run in constant time
  const S = new Float64Array(n + 1),
    Q = new Float64Array(n + 1);
  for (let i = 0; i < n; i++) {
    S[i + 1] = S[i]! + sorted[i]!;
    Q[i + 1] = Q[i]! + sorted[i]! * sorted[i]!;
  }
  const sse = (a: number, b: number) => (b > a ? Q[b]! - Q[a]! - (S[b]! - S[a]!) ** 2 / (b - a) : 0);
  const mean = (a: number, b: number) => (S[b]! - S[a]!) / (b - a);
  const all = sse(0, n);
  const ok = (cuts: number[]) => {
    const edges = [0, ...cuts, n];
    for (let i = 0; i + 1 < edges.length; i++) if (edges[i + 1]! - edges[i]! < 0.08 * n) return false;
    for (let i = 1; i + 1 < edges.length; i++) if (mean(edges[i]!, edges[i + 1]!) - mean(edges[i - 1]!, edges[i]!) < 0.2) return false;
    return true;
  };
  const step = Math.max(1, Math.floor(n / 128));
  let two: { i: number; err: number } | null = null;
  for (let i = step; i < n; i += step) {
    const e = sse(0, i) + sse(i, n);
    if (!two || e < two.err) two = { i, err: e };
  }
  if (!two || two.err >= 0.4 * all || !ok([two.i])) return [mean(0, n)];
  let three: { i: number; j: number; err: number } | null = null;
  for (let i = step; i < n; i += step)
    for (let j = i + step; j < n; j += step) {
      const e = sse(0, i) + sse(i, j) + sse(j, n);
      if (!three || e < three.err) three = { i, j, err: e };
    }
  if (three && three.err < 0.4 * two.err && ok([three.i, three.j])) return [mean(0, three.i), mean(three.i, three.j), mean(three.j, n)];
  return [mean(0, two.i), mean(two.i, n)];
}

function pointInRing(x: number, y: number, r: number[]): boolean {
  let inside = false;
  for (let i = 0, j = r.length - 2; i < r.length; j = i, i += 2) {
    const xi = r[i]!,
      yi = r[i + 1]!,
      xj = r[j]!,
      yj = r[j + 1]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Whether widths along a piece grow (or shrink) steadily, from a line's to several times it: a wedge. */
function isWedge(ws: number[]): boolean {
  const n = ws.length;
  if (n < 5) return false;
  const inner = ws.slice(1, -1);
  const m = inner.length;
  let sx = 0,
    sy = 0,
    sxx = 0,
    sxy = 0,
    syy = 0;
  inner.forEach((w, i) => {
    sx += i;
    sy += w;
    sxx += i * i;
    sxy += i * w;
    syy += w * w;
  });
  const cov = sxy - (sx * sy) / m,
    vx = sxx - (sx * sx) / m,
    vy = syy - (sy * sy) / m;
  if (vx <= 0 || vy <= 1e-9) return false;
  const r = cov / Math.sqrt(vx * vy);
  const lo = Math.min(...inner),
    hi = Math.max(...inner);
  return Math.abs(r) > 0.8 && hi > 2.2 && hi > 2 * Math.max(0.6, lo);
}

/** Whether the ink is solid along a wide piece's middle (not a block of hatching or dots). */
function solidUnder(p: Prim, L: Luma, lk: number, paperAt: (x: number, y: number) => number): boolean {
  let n = 0,
    dark = 0;
  const probe = (x: number, y: number) => {
    const P = Math.max(40, paperAt(x, y));
    n++;
    if ((P - lumaAt(L, x * lk, y * lk)) / P >= 0.5 * p.v) dark++;
  };
  if (p.fit.t === 'line') {
    const len = Math.hypot(p.bx - p.ax, p.by - p.ay);
    const ux = (p.bx - p.ax) / (len || 1),
      uy = (p.by - p.ay) / (len || 1);
    for (let s = 0; s <= len; s += 0.5) for (const o of [-0.3, 0, 0.3]) probe(p.ax + ux * s - uy * o * p.w, p.ay + uy * s + ux * o * p.w);
  } else return true;
  return dark >= 0.95 * n;
}

/** A piece's ends where its fit puts its first and last points. */
function placeEnds(p: Prim): void {
  const n = p.xs.length;
  if (p.fit.t === 'line') {
    const f = p.fit;
    const proj = (i: number) => (p.xs[i]! - f.cx) * f.ux + (p.ys[i]! - f.cy) * f.uy;
    const s0 = proj(0),
      s1 = proj(n - 1);
    p.ax = f.cx + f.ux * s0;
    p.ay = f.cy + f.uy * s0;
    p.bx = f.cx + f.ux * s1;
    p.by = f.cy + f.uy * s1;
  } else {
    const f = p.fit;
    p.ax = f.cx + f.r * Math.cos(f.a0);
    p.ay = f.cy + f.r * Math.sin(f.a0);
    p.bx = f.cx + f.r * Math.cos(f.a1);
    p.by = f.cy + f.r * Math.sin(f.a1);
  }
}

/** The direction a piece runs out of its end ('a': back past its first point; 'b': on past its last). */
function outward(p: Prim, end: 'a' | 'b'): { x: number; y: number } {
  if (p.fit.t === 'line') return end === 'b' ? { x: p.fit.ux, y: p.fit.uy } : { x: -p.fit.ux, y: -p.fit.uy };
  const f = p.fit;
  const a = end === 'b' ? f.a1 : f.a0;
  // the tangent, the way round it runs, reversed at its first end
  const s = (end === 'b' ? 1 : -1) * f.dir;
  return { x: -Math.sin(a) * s, y: Math.cos(a) * s };
}

function refit(xs: Float64Array, ys: Float64Array, tol: number): Fit | null {
  const n = xs.length;
  const l = fitLine(xs, ys, 0, n - 1);
  if (l.err <= tol) return l;
  const c = fitCircle(xs, ys, 0, n - 1, tol);
  if (c) return c;
  return null;
}

function concat(a: Float64Array, b: Float64Array): Float64Array {
  const o = new Float64Array(a.length + b.length);
  o.set(a);
  o.set(b, a.length);
  return o;
}

/**
 * Put the pieces back together where they meet: through each junction a line that passes through
 * is one line; lines ending there meet the line they end on, or each other at their corner; a
 * line's free end is where its ink ends.
 */
function joinAtNodes(prims: Prim[], nodes: NodeC[], L: Luma, lk: number, paperAt: (x: number, y: number) => number): void {
  type End = { p: Prim; end: 'a' | 'b' };
  // the pieces ending at each node, kept up to date as pieces are joined
  const endsAt = new Map<number, Set<Prim>>();
  const index = (p: Prim) => {
    for (const n of [p.na, p.nb]) {
      if (n < 0) continue;
      let s = endsAt.get(n);
      if (!s) endsAt.set(n, (s = new Set()));
      s.add(p);
    }
  };
  const unindex = (p: Prim) => {
    for (const n of [p.na, p.nb]) if (n >= 0) endsAt.get(n)?.delete(p);
  };
  for (const p of prims) index(p);
  const endsOf = (node: number): End[] => {
    const out: End[] = [];
    for (const p of endsAt.get(node) ?? []) {
      if (p.na === node) out.push({ p, end: 'a' });
      if (p.nb === node && p.na !== node) out.push({ p, end: 'b' });
    }
    return out;
  };
  const bars = new Map<number, Prim[]>();
  // 1. through lines: two ends at a node running on in one line (or round one circle) are one piece
  for (let node = 0; node < nodes.length; node++) {
    const tried = new Set<string>();
    for (;;) {
      const ends = endsOf(node);
      let best: { e1: End; e2: End; score: number; key: string } | null = null;
      for (let i = 0; i < ends.length; i++)
        for (let j = i + 1; j < ends.length; j++) {
          const e1 = ends[i]!,
            e2 = ends[j]!;
          if (e1.p === e2.p || e1.p.fit.t !== e2.p.fit.t) continue;
          const key = `${e1.p.id}:${e2.p.id}`;
          if (tried.has(key)) continue;
          const o1 = outward(e1.p, e1.end),
            o2 = outward(e2.p, e2.end);
          const dot = o1.x * o2.x + o1.y * o2.y;
          if (dot > -Math.cos((5 * Math.PI) / 180)) continue;
          const wr = Math.max(e1.p.w, e2.p.w) / Math.max(0.3, Math.min(e1.p.w, e2.p.w));
          if (wr > 1.7 || Math.abs(e1.p.v - e2.p.v) > 0.3) continue;
          if (!best || 1 + dot < best.score) best = { e1, e2, score: 1 + dot, key };
        }
      if (!best) break;
      tried.add(best.key);
      const { e1, e2 } = best;
      const tol = Math.max(0.5, 0.2 * Math.max(e1.p.w, e2.p.w));
      // the points in running order: e1's piece towards the node, then e2's away from it
      const r1 = e1.end === 'b',
        r2 = e2.end === 'a';
      const xs = concat(r1 ? e1.p.xs : e1.p.xs.slice().reverse(), r2 ? e2.p.xs : e2.p.xs.slice().reverse()),
        ys = concat(r1 ? e1.p.ys : e1.p.ys.slice().reverse(), r2 ? e2.p.ys : e2.p.ys.slice().reverse());
      const f = refit(xs, ys, tol);
      if (!f || f.t !== e1.p.fit.t) continue; // not one line after all (a bend at the node)
      const np: Prim = { id: prims.length, fit: f, xs, ys, w: (e1.p.w * e1.p.xs.length + e2.p.w * e2.p.xs.length) / xs.length, v: (e1.p.v * e1.p.xs.length + e2.p.v * e2.p.xs.length) / xs.length, na: r1 ? e1.p.na : e1.p.nb, nb: r2 ? e2.p.nb : e2.p.na, ax: 0, ay: 0, bx: 0, by: 0 };
      // its far ends are where its halves' far ends were (a piece's own end placement, kept)
      placeEnds(np);
      const farA = r1 ? { x: e1.p.ax, y: e1.p.ay } : { x: e1.p.bx, y: e1.p.by },
        farB = r2 ? { x: e2.p.bx, y: e2.p.by } : { x: e2.p.ax, y: e2.p.ay };
      if (np.fit.t === 'line') {
        const g = np.fit;
        const sA = (farA.x - g.cx) * g.ux + (farA.y - g.cy) * g.uy,
          sB = (farB.x - g.cx) * g.ux + (farB.y - g.cy) * g.uy;
        np.ax = g.cx + g.ux * sA;
        np.ay = g.cy + g.uy * sA;
        np.bx = g.cx + g.ux * sB;
        np.by = g.cy + g.uy * sB;
      }
      unindex(e1.p);
      unindex(e2.p);
      e1.p.dead = true;
      e2.p.dead = true;
      e1.p.succ = np;
      e2.p.succ = np;
      prims.push(np);
      index(np);
      const bl = bars.get(node);
      if (bl) bl.push(np);
      else bars.set(node, [np]);
    }
  }
  // (a bar recorded at a node may have been joined on at another node since: the piece it became)
  const live = (p: Prim): Prim => {
    let q = p;
    while (q.succ) q = q.succ;
    return q;
  };
  // 2. the ends left at each node meet: the bar they end on, or each other at their corner
  for (let node = 0; node < nodes.length; node++) {
    const nd = nodes[node]!;
    const ends = endsOf(node);
    if (!ends.length) continue;
    const through: Prim[] = [];
    for (const b of bars.get(node) ?? []) {
      const q = live(b);
      if (!q.dead && !through.includes(q)) through.push(q);
    }
    const reach = (e: End) => 3 * e.p.w + 4;
    const setEnd = (e: End, x: number, y: number) => {
      if (e.end === 'a') {
        e.p.ax = x;
        e.p.ay = y;
      } else {
        e.p.bx = x;
        e.p.by = y;
      }
    };
    for (const e of ends) {
      if (e.p.fit.t !== 'line') continue;
      const o = outward(e.p, e.end);
      const from = e.end === 'a' ? { x: e.p.ax, y: e.p.ay } : { x: e.p.bx, y: e.p.by };
      let hit: { x: number; y: number; t: number } | null = null;
      for (const b of through) {
        if (b === e.p) continue;
        const q = rayMeets(from, o, b);
        if (q && Math.abs(q.t) <= reach(e) + Math.hypot(from.x - nd.x, from.y - nd.y) && (!hit || Math.abs(q.t) < Math.abs(hit.t))) hit = q;
      }
      if (!hit && !through.length && ends.length === 2) {
        // a corner: where the two lines cross
        const other = ends.find((x) => x !== e)!;
        if (other.p.fit.t === 'line') {
          const oo = outward(other.p, other.end);
          const cross = o.x * oo.y - o.y * oo.x;
          if (Math.abs(cross) > Math.sin((12 * Math.PI) / 180)) {
            const of = other.end === 'a' ? { x: other.p.ax, y: other.p.ay } : { x: other.p.bx, y: other.p.by };
            const t = ((of.x - from.x) * oo.y - (of.y - from.y) * oo.x) / cross;
            if (Math.abs(t) <= reach(e) + Math.hypot(from.x - nd.x, from.y - nd.y)) hit = { x: from.x + o.x * t, y: from.y + o.y * t, t };
          }
        }
      }
      if (!hit && ends.length + through.length >= 2) {
        // several meet: to where the node is, along the line
        const t = (nd.x - from.x) * o.x + (nd.y - from.y) * o.y;
        if (t > 0 && t <= reach(e) + 2) hit = { x: from.x + o.x * t, y: from.y + o.y * t, t };
      }
      if (hit) setEnd(e, hit.x, hit.y);
    }
  }
  // 3. a free end runs on to where its ink ends
  for (const p of prims) {
    if (p.dead || p.fit.t !== 'line') continue;
    for (const end of ['a', 'b'] as const) {
      const node = end === 'a' ? p.na : p.nb;
      if (node >= 0 && nodes[node]!.ends > 1) continue;
      const o = outward(p, end);
      const from = end === 'a' ? { x: p.ax, y: p.ay } : { x: p.bx, y: p.by };
      const P = Math.max(40, paperAt(from.x, from.y));
      let t = 0;
      for (let s = 0.25; s <= 3 * p.w + 4; s += 0.25) {
        const d = (P - lumaAt(L, (from.x + o.x * s) * lk, (from.y + o.y * s) * lk)) / P;
        if (d < 0.5 * p.v) break;
        t = s;
      }
      // (drawn with round ends: the end's centre is half its width inside the ink's end)
      t = Math.max(0, t - p.w / 2);
      if (end === 'a') {
        p.ax = from.x + o.x * t;
        p.ay = from.y + o.y * t;
      } else {
        p.bx = from.x + o.x * t;
        p.by = from.y + o.y * t;
      }
    }
  }
}

/** Where a ray from `from` along `u` meets a line piece (t: how far along the ray), or null. */
function rayMeets(from: { x: number; y: number }, u: { x: number; y: number }, b: Prim): { x: number; y: number; t: number } | null {
  if (b.fit.t !== 'line') return null;
  const f = b.fit as LineFit;
  const cross = u.x * f.uy - u.y * f.ux;
  if (Math.abs(cross) < 0.05) return null;
  const t = ((f.cx - from.x) * f.uy - (f.cy - from.y) * f.ux) / cross;
  return { x: from.x + u.x * t, y: from.y + u.y * t, t };
}

function toItem(p: Prim): DItem {
  const v = Math.max(0.05, Math.min(1, p.v));
  if (p.fit.t === 'line') return { t: 'line', x1: p.ax, y1: p.ay, x2: p.bx, y2: p.by, w: p.w, v, role: 'plan' };
  const f = p.fit as ArcFit;
  const a0 = Math.atan2(p.ay - f.cy, p.ax - f.cx);
  let a1 = Math.atan2(p.by - f.cy, p.bx - f.cx);
  // keep its sweep the way (and nearly as far) round as fitted
  const sweep = f.a1 - f.a0;
  while (a1 - a0 - sweep > Math.PI) a1 -= 2 * Math.PI;
  while (a1 - a0 - sweep < -Math.PI) a1 += 2 * Math.PI;
  return { t: 'arc', cx: f.cx, cy: f.cy, r: f.r, a0, a1, w: p.w, v, role: 'plan' };
}

/** Points along a line or arc, every `step` px (image px), ends included. */
function sampleAlong(it: DLine | DArc, step: number, visit: (x: number, y: number) => void): void {
  if (it.t === 'line') {
    const len = Math.hypot(it.x2 - it.x1, it.y2 - it.y1);
    const n = Math.max(1, Math.ceil(len / step));
    for (let i = 0; i <= n; i++) visit(it.x1 + ((it.x2 - it.x1) * i) / n, it.y1 + ((it.y2 - it.y1) * i) / n);
  } else {
    const n = Math.max(1, Math.ceil((Math.abs(it.a1 - it.a0) * it.r) / step));
    for (let i = 0; i <= n; i++) {
      const a = it.a0 + ((it.a1 - it.a0) * i) / n;
      visit(it.cx + it.r * Math.cos(a), it.cy + it.r * Math.sin(a));
    }
  }
}

/** The largest value within `r` pixels (a square) of each pixel of a W × H grid (of values ≥ 0). */
function localMax(f: Float32Array, W: number, H: number, r: number): Float32Array {
  const a = new Float32Array(W * H),
    b = new Float32Array(W * H);
  // along the rows, then down the columns (a row at a time, so every pass reads in order)
  for (let y = 0; y < H; y++) {
    const o = y * W;
    for (let x = 0; x < W; x++) {
      const lo = x - r < 0 ? 0 : x - r,
        hi = x + r >= W ? W - 1 : x + r;
      let m = f[o + lo]!;
      for (let d = lo + 1; d <= hi; d++) {
        const v = f[o + d]!;
        if (v > m) m = v;
      }
      a[o + x] = m;
    }
  }
  for (let y = 0; y < H; y++) {
    const lo = y - r < 0 ? 0 : y - r,
      hi = y + r >= H ? H - 1 : y + r;
    const o = y * W;
    b.set(a.subarray(lo * W, lo * W + W), o);
    for (let d = lo + 1; d <= hi; d++) {
      const od = d * W;
      for (let x = 0; x < W; x++) {
        const v = a[od + x]!;
        if (v > b[o + x]!) b[o + x] = v;
      }
    }
  }
  return b;
}

/* ------------------------------------------------------------------ dashed lines */

/** Spans along a line, sorted and with the overlapping ones merged; then as a dash pattern (on, off, on …). */
function dashPattern(spans: (readonly [number, number])[]): { s0: number; s1: number; dash: number[] } {
  const sorted = spans.slice().sort((p, q) => p[0] - q[0]);
  const merged: [number, number][] = [];
  for (const [p, q] of sorted) {
    const last = merged[merged.length - 1];
    if (last && p <= last[1] + 0.25) last[1] = Math.max(last[1], q);
    else merged.push([p, q]);
  }
  const dash: number[] = [];
  let at = merged[0]![0];
  for (const [p, q] of merged) {
    if (dash.length) dash.push(p - at);
    dash.push(Math.max(0.01, q - p));
    at = q;
  }
  return { s0: merged[0]![0], s1: merged[merged.length - 1]![1], dash };
}

/**
 * A dashed (or dash-dot) line is one line: its dashes — lines in line with one another, of one
 * weight and tone, with gaps between them — are joined into one line drawn in the dashes the plan
 * draws (each dash where it is, each gap as long as it is). A dot of a dash-dot line (an outline
 * too small to be a line) in line with the dashes is one of them.
 */
function groupDashes(items: DItem[]): DItem[] {
  const lines = items.filter((it): it is DLine => it.t === 'line' && it.role === 'plan' && !it.dash);
  const n = lines.length;
  const parent = lines.map((_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i]!)));
  const dir = lines.map((l) => {
    const len = Math.hypot(l.x2 - l.x1, l.y2 - l.y1) || 1e-9;
    return { x: (l.x2 - l.x1) / len, y: (l.y2 - l.y1) / len, len };
  });
  // a grid of line ends, to find neighbours quickly
  const CELL = 48;
  const grid = new Map<number, number[]>();
  const key = (x: number, y: number) => Math.floor(y / CELL) * 100000 + Math.floor(x / CELL);
  lines.forEach((l, i) => {
    for (const [x, y] of [
      [l.x1, l.y1],
      [l.x2, l.y2],
    ] as const) {
      const kk = key(x, y);
      const a = grid.get(kk);
      if (a) a.push(i);
      else grid.set(kk, [i]);
    }
  });
  const COS = Math.cos((2 * Math.PI) / 180);
  for (let i = 0; i < n; i++) {
    const a = lines[i]!,
      da = dir[i]!;
    const reach = Math.min(48, Math.max(6, 5 * a.w, 1.2 * da.len));
    for (const [ex, ey] of [
      [a.x1, a.y1],
      [a.x2, a.y2],
    ] as const) {
      for (let gy = Math.floor((ey - reach) / CELL); gy <= Math.floor((ey + reach) / CELL); gy++)
        for (let gx = Math.floor((ex - reach) / CELL); gx <= Math.floor((ex + reach) / CELL); gx++)
          for (const j of grid.get(gy * 100000 + gx) ?? []) {
            if (j <= i) continue;
            const b = lines[j]!,
              db = dir[j]!;
            if (Math.abs(da.x * db.x + da.y * db.y) < COS) continue;
            if (Math.max(a.w, b.w) > 1.6 * Math.min(a.w, b.w) + 0.4 || Math.abs(a.v - b.v) > 0.2) continue;
            // in one line: b's ends on a's line, within a fraction of the width
            const off = (x: number, y: number) => Math.abs((x - a.x1) * da.y - (y - a.y1) * da.x);
            const tol = Math.max(0.8, 0.4 * a.w);
            if (off(b.x1, b.y1) > tol || off(b.x2, b.y2) > tol) continue;
            // with a gap between them (not overlapping, not far)
            const s = (x: number, y: number) => (x - a.x1) * da.x + (y - a.y1) * da.y;
            const b0 = Math.min(s(b.x1, b.y1), s(b.x2, b.y2)),
              b1 = Math.max(s(b.x1, b.y1), s(b.x2, b.y2));
            const gap = b0 > da.len ? b0 - da.len : b1 < 0 ? -b1 : -1;
            if (gap <= 0 || gap > reach) continue;
            parent[find(j)] = find(i);
          }
    }
  }
  const groups = new Map<number, number[]>();
  for (let i = 0; i < n; i++) {
    const r = find(i);
    const g = groups.get(r);
    if (g) g.push(i);
    else groups.set(r, [i]);
  }
  const gone = new Set<DItem>();
  const made: (DLine | DArc)[] = [];
  for (const members of groups.values()) {
    if (members.length < 3) continue;
    // the line through all its dashes' ends
    const xs = new Float64Array(members.length * 2),
      ys = new Float64Array(members.length * 2);
    members.forEach((m, i) => {
      const l = lines[m]!;
      xs[2 * i] = l.x1;
      ys[2 * i] = l.y1;
      xs[2 * i + 1] = l.x2;
      ys[2 * i + 1] = l.y2;
    });
    const f = fitLine(xs, ys, 0, xs.length - 1);
    const tolG = Math.max(0.8, 0.4 * Math.max(...members.map((m) => lines[m]!.w)));
    if (f.err > tolG) {
      // not straight: dashes round a curve are a dashed arc, if one circle holds them all
      const mx = new Float64Array(members.length * 3),
        my = new Float64Array(members.length * 3);
      // (in order along the curve: by their position along the group's own chord)
      const order = members.slice().sort((p, q) => {
        const lp = lines[p]!,
          lq = lines[q]!;
        return (lp.x1 + lp.x2 - lq.x1 - lq.x2) * f.ux + (lp.y1 + lp.y2 - lq.y1 - lq.y2) * f.uy;
      });
      order.forEach((m, i) => {
        const l = lines[m]!;
        const fwd = (l.x2 - l.x1) * f.ux + (l.y2 - l.y1) * f.uy >= 0;
        const [ax, ay, bx, by] = fwd ? [l.x1, l.y1, l.x2, l.y2] : [l.x2, l.y2, l.x1, l.y1];
        mx[3 * i] = ax;
        my[3 * i] = ay;
        mx[3 * i + 1] = (ax + bx) / 2;
        my[3 * i + 1] = (ay + by) / 2;
        mx[3 * i + 2] = bx;
        my[3 * i + 2] = by;
      });
      const c = fitCircle(mx, my, 0, mx.length - 1);
      if (!c || c.err > tolG) continue;
      const ang = (x: number, y: number) => {
        let a = Math.atan2(y - c.cy, x - c.cx);
        while ((a - c.a0) * c.dir < -1e-9) a += 2 * Math.PI * c.dir;
        return a;
      };
      const spansA = order
        .map((m) => {
          const l = lines[m]!;
          const p = (ang(l.x1, l.y1) - c.a0) * c.dir * c.r,
            q = (ang(l.x2, l.y2) - c.a0) * c.dir * c.r;
          return [Math.min(p, q), Math.max(p, q)] as const;
        })
        .sort((p, q) => p[0] - q[0]);
      const pa = dashPattern(spansA);
      const dashA = pa.dash;
      const wsA = members.map((m) => lines[m]!.w).sort((p, q) => p - q),
        vsA = members.map((m) => lines[m]!.v).sort((p, q) => p - q);
      made.push({ t: 'arc', cx: c.cx, cy: c.cy, r: c.r, a0: c.a0 + (c.dir * pa.s0) / c.r, a1: c.a0 + (c.dir * pa.s1) / c.r, w: wsA[wsA.length >> 1]!, v: vsA[vsA.length >> 1]!, role: 'plan', dash: dashA });
      for (const m of members) gone.add(lines[m]!);
      continue;
    }
    const spans = members
      .map((m) => {
        const l = lines[m]!;
        const s1 = (l.x1 - f.cx) * f.ux + (l.y1 - f.cy) * f.uy,
          s2 = (l.x2 - f.cx) * f.ux + (l.y2 - f.cy) * f.uy;
        return [Math.min(s1, s2), Math.max(s1, s2)] as const;
      })
      .sort((p, q) => p[0] - q[0]);
    const { s0, s1, dash } = dashPattern(spans);
    const ws = members.map((m) => lines[m]!.w).sort((p, q) => p - q),
      vs = members.map((m) => lines[m]!.v).sort((p, q) => p - q);
    made.push({ t: 'line', x1: f.cx + f.ux * s0, y1: f.cy + f.uy * s0, x2: f.cx + f.ux * s1, y2: f.cy + f.uy * s1, w: ws[ws.length >> 1]!, v: vs[vs.length >> 1]!, role: 'plan', dash });
    for (const m of members) gone.add(lines[m]!);
  }
  return [...items.filter((it) => !gone.has(it)), ...made];
}

/* ------------------------------------------------------------------ leaders */

/**
 * Leaders are not the plan's line work: each line of text is followed along its leader (from the
 * text's end, along its landing and round its one bend, straight on to its tip: stations.ts), and
 * the lines lying along that path are the leader; an outline at its tip is its arrowhead.
 */
function markLeaders(inp: DrawingInput, items: DItem[]): Leader[] {
  const { plan, comps, labels } = inp;
  const k = plan.k;
  const lines = findTextLines(comps, labels, plan.cw);
  const ink = (x: number, y: number) => {
    const X = Math.floor(x * k),
      Y = Math.floor(y * k);
    return X >= 0 && Y >= 0 && X < plan.cw && Y < plan.ch && plan.lineMask[Y * plan.cw + X] === 1;
  };
  // how far ink runs from p along u (a pixel either side of the way), until it gives out
  const inkRun = (p: { x: number; y: number }, u: { x: number; y: number }, reach: number): number => {
    const px = 1 / k;
    let len = 0,
      miss = 0;
    for (let t = px; t <= reach; t += 0.5 * px) {
      const x = p.x + u.x * t,
        y = p.y + u.y * t;
      if (ink(x, y) || ink(x - u.y * px, y + u.x * px) || ink(x + u.y * px, y - u.x * px)) {
        len = t;
        miss = 0;
      } else if ((miss += 0.5 * px) > 1.5 * px) break;
    }
    return len;
  };
  // the text lines' boxes (image px), to know a line that runs from one label to another
  const boxes = lines.map((l) => ({ x0: l.minX / k, x1: (l.maxX + 1) / k, y0: (l.base - l.H) / k, y1: l.base / k, H: l.H / k }));
  const nearText = (p: { x: number; y: number }, not: number) =>
    boxes.some((b, i) => i !== not && p.x >= b.x0 - b.H && p.x <= b.x1 + b.H && p.y >= b.y0 - 0.5 * b.H && p.y <= b.y1 + 0.5 * b.H);
  const edge = 3;
  const paths: { a: { x: number; y: number }; b: { x: number; y: number } }[] = [];
  const tips: { x: number; y: number; dir: { x: number; y: number } }[] = [];
  const leaders: Leader[] = [];
  lines.forEach((line, li) => {
    const H = line.H / k,
      mid = (line.base - 0.5 * line.H) / k;
    const tries = ([1, -1] as const).map((side) => followLeader(plan, null, { x: side > 0 ? (line.maxX + 1) / k : line.minX / k, y: mid }, side, H)).filter((l): l is NonNullable<typeof l> => !!l);
    tries.sort((p, q) => p.gap - q.gap);
    const l = tries[0];
    if (!l) return;
    // (a line leaving the text at both ends, one way on, is one the text is lettered into: a
    // contour's height, a pipe's size — never a leader)
    if (tries.length === 2 && tries[1]!.gap <= 0.5 * H && tries[0]!.dir.x * tries[1]!.dir.x + tries[0]!.dir.y * tries[1]!.dir.y < -Math.cos((35 * Math.PI) / 180)) return;
    // a leader is plainly one: it ends on the plan (a line running off the picture is the plan's
    // own), it starts where the text is (a line running on back past its start is one passing the
    // text by), and a level one runs to something other than more text (a line from one label to
    // the next is that line, labelled)
    if (l.tip.x < edge || l.tip.y < edge || l.tip.x > plan.w - edge || l.tip.y > plan.h - edge) return;
    if (inkRun(l.start, { x: -l.dir.x, y: -l.dir.y }, 1.5 * H) >= 0.8 * H && Math.abs(l.dir.y) > 0.2) return;
    if (Math.abs(l.dir.y) < 0.2 && nearText(l.tip, li)) return;
    // a level one points (an arrowhead or a dot at its tip) and keeps level
    if (Math.abs(l.dir.y) < 0.2) {
      if (Math.abs(l.tip.y - l.start.y) > 0.35 * H) return;
      const pointed = items.some((it) => {
        if (it.t !== 'fill') return false;
        const r = it.rings[0]!;
        let x0 = Infinity,
          y0 = Infinity,
          x1 = -Infinity,
          y1 = -Infinity;
        for (let i = 0; i < r.length; i += 2) {
          x0 = Math.min(x0, r[i]!);
          x1 = Math.max(x1, r[i]!);
          y0 = Math.min(y0, r[i + 1]!);
          y1 = Math.max(y1, r[i + 1]!);
        }
        return x1 - x0 <= 1.5 * H && y1 - y0 <= 1.5 * H && l.tip.x >= x0 - 3 && l.tip.x <= x1 + 3 && l.tip.y >= y0 - 3 && l.tip.y <= y1 + 3 && (x1 - x0) * (y1 - y0) >= 4;
      });
      if (!pointed) return;
    }
    // it leaves the text close by, and an angled one from a landing (the short level stroke at the
    // text that every drawn leader starts with)
    if (l.gap > 0.6 * H) return;
    const land = Math.hypot(l.start.x - l.landing.x, l.start.y - l.landing.y);
    if (Math.abs(l.dir.y) >= 0.2 && (land < 0.3 * H || land > 2 * H)) return;
    // and it ends there: no line runs on from its tip the way it was going (a line curving away
    // from a straight march is the plan's; an arrowhead, a small circle, a line it stops on are not)
    for (const da of [0, -7, 7, -14, 14, -20, 20]) {
      const a = Math.atan2(l.dir.y, l.dir.x) + (da * Math.PI) / 180;
      if (inkRun(l.tip, { x: Math.cos(a), y: Math.sin(a) }, 3 * H) >= 1.2 * H) return;
    }
    // a line running straight on out of both ends of the text (the text lettered into a break of the
    // line: "8" RW") is that line, labelled — never a leader; nor is a level one from text the
    // picture's edge cuts into (the line may run on past it, out of sight)
    if (Math.abs(l.dir.y) < 0.2) {
      const sides = [line.minX / k, (line.maxX + 1) / k];
      if (sides[0]! < 3.5 * H || sides[1]! > plan.w - 3.5 * H) return;
      const both = sides.every((ex, i) => {
        const out = i === 0 ? -1 : 1;
        let run = 0;
        for (let d = 0.5 * H; d <= 3.5 * H; d += 0.5) {
          const y = mid;
          if (ink(ex + out * d, y) || ink(ex + out * d, y - 1 / k) || ink(ex + out * d, y + 1 / k)) run += 0.5;
        }
        return run >= 1.8 * H;
      });
      if (both) return;
    }
    paths.push({ a: l.landing, b: l.start });
    paths.push({ a: l.start, b: l.tip });
    tips.push({ x: l.tip.x, y: l.tip.y, dir: l.dir });
    const pts = [l.landing.x, l.landing.y];
    if (Math.hypot(l.start.x - l.landing.x, l.start.y - l.landing.y) > 0.5) pts.push(l.start.x, l.start.y);
    pts.push(l.tip.x, l.tip.y);
    leaders.push({ pts, text: { x0: boxes[li]!.x0, y0: boxes[li]!.y0, x1: boxes[li]!.x1, y1: boxes[li]!.y1 } });
  });
  const along = (x: number, y: number, p: { a: { x: number; y: number }; b: { x: number; y: number } }, tol: number) => {
    const ex = p.b.x - p.a.x,
      ey = p.b.y - p.a.y;
    const l2 = ex * ex + ey * ey || 1e-9;
    const t = ((x - p.a.x) * ex + (y - p.a.y) * ey) / l2;
    if (t < -0.02 || t > 1.02) return false;
    return Math.hypot(x - p.a.x - ex * t, y - p.a.y - ey * t) <= tol;
  };
  // a line (or a faint leader's outline) lying all but wholly along a leader is that leader
  const onLeader = (x: number, y: number, tol: number) => paths.some((p) => along(x, y, p, tol));
  for (const it of items) {
    if (it.t === 'line') {
      const tol = Math.max(1.5, it.w);
      let n = 0,
        on = 0;
      sampleAlong(it, 1, (x, y) => {
        n++;
        if (onLeader(x, y, tol)) on++;
      });
      if (n && on >= 0.9 * n) it.role = 'leader';
    } else if (it.t === 'fill' && it.role === 'symbol' && it.rings.length === 1) {
      const r = it.rings[0]!;
      let on = 0;
      for (let i = 0; i < r.length; i += 2) if (onLeader(r[i]!, r[i + 1]!, 2.5)) on++;
      if (on >= 0.9 * (r.length / 2) && Math.abs(ringArea(r)) < 60 * (r.length / 2)) it.role = 'leader';
    }
  }
  for (const it of items) {
    if (it.t !== 'fill') continue;
    const r = it.rings[0]!;
    let cx = 0,
      cy = 0;
    for (let i = 0; i < r.length; i += 2) {
      cx += r[i]!;
      cy += r[i + 1]!;
    }
    cx /= r.length / 2;
    cy /= r.length / 2;
    if (tips.some((t) => Math.hypot(t.x - cx, t.y - cy) <= 8)) it.role = 'arrowhead';
  }
  return leaders;
}

/* ------------------------------------------------------------------ drawing it */

type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/** Draw the drawing (image px × `scale`) in the plan's own tones on paper (`paper`: 0–255). */
export function paintDrawing(ctx: Ctx, d: Drawing, scale: number, colour?: (it: DItem) => string | null): void {
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const it of d.items) {
    const tone = Math.round(255 * (1 - it.v));
    const col = colour?.(it) ?? `rgb(${tone},${tone},${tone})`;
    if (it.t === 'line' || it.t === 'arc') {
      ctx.strokeStyle = col;
      ctx.lineWidth = Math.max(0.5, it.w) * scale;
      ctx.beginPath();
      if (it.t === 'line') {
        ctx.moveTo(it.x1 * scale, it.y1 * scale);
        ctx.lineTo(it.x2 * scale, it.y2 * scale);
      } else {
        // as a polyline true to a twentieth of a pixel (a canvas arc of a great radius is not drawn true)
        const r = it.r * scale;
        const step = Math.min(0.2, 2 * Math.acos(Math.max(-1, 1 - 0.05 / Math.max(r, 0.05))));
        const n = Math.max(2, Math.ceil(Math.abs(it.a1 - it.a0) / step));
        for (let i = 0; i <= n; i++) {
          const a = it.a0 + ((it.a1 - it.a0) * i) / n;
          const x = it.cx * scale + r * Math.cos(a),
            y = it.cy * scale + r * Math.sin(a);
          if (i) ctx.lineTo(x, y);
          else ctx.moveTo(x, y);
        }
      }
      if (it.dash) ctx.setLineDash([...it.dash, 1e6].map((v) => v * scale));
      ctx.stroke();
      ctx.setLineDash([]);
    } else if (it.t === 'fill') {
      ctx.fillStyle = col;
      ctx.beginPath();
      for (const r of it.rings) {
        ctx.moveTo(r[0]! * scale, r[1]! * scale);
        for (let i = 2; i < r.length; i += 2) ctx.lineTo(r[i]! * scale, r[i + 1]! * scale);
        ctx.closePath();
      }
      ctx.fill('evenodd');
    }
  }
  ctx.restore();
}
